import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { CircularProgress, FormControl, InputLabel, Select, MenuItem, Alert } from '@mui/material';
import rootApi from '@utils/baseAPI/WrapperApi';
import { useMutation, useQuery } from 'react-query';
import { showError, showSuccess } from '@components/notification';
import { useNavigate } from 'react-router-dom';

const DialogStartCharging = (props) => {
  const { customer, open, setOpen } = props;
  const navigate = useNavigate();

  const [selectedDepot, setSelectedDepot] = useState('');
  const [selectedStation, setSelectedStation] = useState('');
  const [selectedConnector, setSelectedConnector] = useState('');
  const [stations, setStations] = useState([]);
  const [connectors, setConnectors] = useState([]);

  // Fetch danh sách depots
  const { data: depotsData, isLoading: loadingDepots } = useQuery(
    ['all-depots-for-charging'],
    () => rootApi.get('/depots?page=0&size=1000'),
    { enabled: open }
  );

  const depots = depotsData?.data?.data || [];

  // Fetch chi tiết depot khi chọn depot
  const { data: depotDetail, isLoading: loadingDetail } = useQuery(
    ['depot-detail', selectedDepot],
    () => rootApi.get(`/depots/detail?depot_code=${selectedDepot}`),
    { enabled: !!selectedDepot }
  );

  // Update stations khi có depot detail
  useEffect(() => {
    if (depotDetail?.data?.data) {
      setStations(depotDetail.data.data.station_responses || []);
      setSelectedStation('');
      setSelectedConnector('');
    }
  }, [depotDetail]);

  // Update connectors khi chọn station
  useEffect(() => {
    if (selectedStation) {
      const station = stations.find((s) => s.id === selectedStation);
      setConnectors(station?.connector_responses || []);
      setSelectedConnector('');
    }
  }, [selectedStation, stations]);

  // Start charging mutation
  const { mutateAsync, isLoading: isStarting } = useMutation('admin-start-charging', (requestData) =>
    rootApi.post('/admin/transactions/start-for-user', requestData)
  );

  const handleClose = () => {
    setOpen(false);
    setSelectedDepot('');
    setSelectedStation('');
    setSelectedConnector('');
  };

  const handleStartCharging = async () => {
    if (!selectedDepot || !selectedConnector) {
      showError({ message: 'Vui lòng chọn đầy đủ trạm và súng sạc!' });
      return;
    }

    const connector = connectors.find((c) => c.id === selectedConnector);

    try {
      const response = await mutateAsync({
        user_id: customer.id,
        depot_code: selectedDepot,
        station_id: selectedStation,
        connector_id: selectedConnector,
        qr_code: connector?.qr_code,
        voucher_id: null // Có thể thêm voucher selector nếu cần
      });

      showSuccess({ message: 'Bắt đầu sạc thành công!' });
      handleClose();

      // Redirect to transaction detail
      const transactionId = response?.data?.data?.transaction_id;
      if (transactionId) {
        navigate(`/admin/charging-session/${transactionId}/detail`);
      }
    } catch (error) {
      showError(error || 'Không thể bắt đầu phiên sạc. Vui lòng thử lại!');
    }
  };

  const availableConnectors = connectors.filter((c) => c.status === 'Available' || c.status === 'Preparing');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bắt đầu sạc cho khách hàng</DialogTitle>
      <DialogContent>
        {customer && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Khách hàng: <strong>{customer.username}</strong> ({customer.full_name})
          </Alert>
        )}

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Chọn trạm sạc</InputLabel>
          <Select
            value={selectedDepot}
            label="Chọn trạm sạc"
            onChange={(e) => setSelectedDepot(e.target.value)}
            disabled={loadingDepots}>
            {depots.map((depot) => (
              <MenuItem key={depot.depot_code} value={depot.depot_code}>
                {depot.depot_name} ({depot.depot_code}) - {depot.number_connector_available} súng
                available
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Chọn trụ sạc (station)</InputLabel>
          <Select
            value={selectedStation}
            label="Chọn trụ sạc (station)"
            onChange={(e) => setSelectedStation(e.target.value)}
            disabled={!selectedDepot || loadingDetail}>
            {stations.map((station) => (
              <MenuItem key={station.id} value={station.id}>
                {station.identifier} ({station.status})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Chọn súng sạc</InputLabel>
          <Select
            value={selectedConnector}
            label="Chọn súng sạc"
            onChange={(e) => setSelectedConnector(e.target.value)}
            disabled={!selectedStation}>
            {availableConnectors.length > 0 ? (
              availableConnectors.map((connector) => (
                <MenuItem key={connector.id} value={connector.id}>
                  {connector.qr_code || `Súng ${connector.connector_id}`} - {connector.status}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>Không có súng sạc khả dụng</MenuItem>
            )}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" color="inherit">
          Hủy
        </Button>
        <Button
          onClick={handleStartCharging}
          variant="contained"
          color="primary"
          disabled={isStarting || !selectedConnector || availableConnectors.length === 0}>
          {isStarting && <CircularProgress size={20} sx={{ mr: 1 }} />} Bắt đầu sạc
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogStartCharging;
